import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, Search, Download, CheckCircle, Clock, FileText, 
  User, Eye, Save, X, MessageSquare
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TeacherSubmissions() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || 'all');
  const [filterAssignment, setFilterAssignment] = useState(searchParams.get('assignment_id') || 'all');
  const [showGradeModal, setShowGradeModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [grading, setGrading] = useState(false);

  const [gradeData, setGradeData] = useState({
    marks: 0,
    feedback: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const getHeaders = () => {
    const token = localStorage.getItem('staffToken');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        navigate('/staff-portal');
        return;
      }

      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [submissionsRes, assignmentsRes] = await Promise.all([
        fetch(`${API_URL}/api/lms/submissions`, { headers }).then(r => r.json()).catch(() => []),
        fetch(`${API_URL}/api/lms/assignments`, { headers }).then(r => r.json()).catch(() => [])
      ]);

      setSubmissions(Array.isArray(submissionsRes) ? submissionsRes : []);
      setAssignments(Array.isArray(assignmentsRes) ? assignmentsRes : []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const openGradeModal = (submission) => {
    setSelectedSubmission(submission);
    setGradeData({
      marks: submission.marks || 0,
      feedback: submission.feedback || ''
    });
    setShowGradeModal(true);
  };

  const handleGrade = async () => {
    if (!selectedSubmission) return;

    setGrading(true);
    try {
      const response = await fetch(`${API_URL}/api/lms/submissions/${selectedSubmission.id}/grade`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(gradeData)
      });

      if (response.ok) {
        toast.success('Submission graded successfully!');
        setShowGradeModal(false);
        fetchData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to grade');
      }
    } catch (error) {
      toast.error('Failed to grade submission');
    } finally {
      setGrading(false);
    }
  };

  const downloadFile = (submission) => {
    if (submission.file_data) {
      const link = document.createElement('a');
      link.href = submission.file_data;
      link.download = submission.file_name || 'submission';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (submission.file_url) {
      window.open(submission.file_url, '_blank');
    } else {
      toast.error('No file attached');
    }
  };

  const getStatusBadge = (submission) => {
    if (submission.status === 'graded' || submission.marks !== undefined) {
      return <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded flex items-center gap-1"><CheckCircle className="h-3 w-3" />Graded</span>;
    }
    return <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded flex items-center gap-1"><Clock className="h-3 w-3" />Pending</span>;
  };

  const filteredSubmissions = submissions.filter(s => {
    const matchSearch = 
      s.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.assignment_title?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = filterStatus === 'all' || 
      (filterStatus === 'pending' && s.status !== 'graded') ||
      (filterStatus === 'graded' && s.status === 'graded');
    const matchAssignment = filterAssignment === 'all' || s.assignment_id === filterAssignment;
    return matchSearch && matchStatus && matchAssignment;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/staff-portal/lms" className="p-2 hover:bg-indigo-600 rounded-lg">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold">Review Submissions</h1>
              <p className="text-indigo-200 text-sm">Grade student assignment submissions</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-gray-900">{submissions.length}</p>
              <p className="text-sm text-gray-500">Total Submissions</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-yellow-600">{submissions.filter(s => s.status !== 'graded').length}</p>
              <p className="text-sm text-gray-500">Pending Review</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-600">{submissions.filter(s => s.status === 'graded').length}</p>
              <p className="text-sm text-gray-500">Graded</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input placeholder="Search by student or assignment..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
              </div>
              <Select value={filterAssignment} onValueChange={setFilterAssignment}>
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue placeholder="All Assignments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Assignments</SelectItem>
                  {assignments.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full md:w-36">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="graded">Graded</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Submissions List */}
        <div className="space-y-4">
          {filteredSubmissions.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No submissions found</p>
              </CardContent>
            </Card>
          ) : (
            filteredSubmissions.map(submission => (
              <Card key={submission.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-orange-100 rounded-lg">
                        <User className="h-6 w-6 text-orange-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900">{submission.student_name}</h3>
                          {getStatusBadge(submission)}
                        </div>
                        <p className="text-sm text-gray-600 font-medium">{submission.assignment_title}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Submitted: {submission.submitted_at?.split('T')[0]} {submission.submitted_at?.split('T')[1]?.slice(0,5)}
                        </p>
                        {submission.marks !== undefined && (
                          <p className="text-sm mt-2">
                            <span className="font-semibold text-green-600">{submission.marks}</span>
                            <span className="text-gray-500">/{submission.total_marks || 100} marks</span>
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      {(submission.file_data || submission.file_url) && (
                        <Button variant="outline" size="sm" onClick={() => downloadFile(submission)}>
                          <Download className="h-4 w-4 mr-1" />
                          Download
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        onClick={() => openGradeModal(submission)}
                        className={submission.status === 'graded' ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700'}
                      >
                        {submission.status === 'graded' ? 'Edit Grade' : 'Grade Now'}
                      </Button>
                    </div>
                  </div>
                  
                  {submission.answer && (
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-700">{submission.answer}</p>
                    </div>
                  )}
                  
                  {submission.feedback && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg flex items-start gap-2">
                      <MessageSquare className="h-4 w-4 text-blue-600 mt-0.5" />
                      <p className="text-sm text-blue-800">{submission.feedback}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </main>

      {/* Grade Modal */}
      {showGradeModal && selectedSubmission && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">Grade Submission</h2>
              <button onClick={() => setShowGradeModal(false)}><X className="h-5 w-5" /></button>
            </div>
            
            <div className="p-4 space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="font-medium">{selectedSubmission.student_name}</p>
                <p className="text-sm text-gray-500">{selectedSubmission.assignment_title}</p>
              </div>
              
              <div>
                <Label>Marks (out of {selectedSubmission.total_marks || 100})</Label>
                <Input 
                  type="number" 
                  value={gradeData.marks} 
                  onChange={(e) => setGradeData(prev => ({ ...prev, marks: parseInt(e.target.value) || 0 }))}
                  max={selectedSubmission.total_marks || 100}
                  min={0}
                />
              </div>
              
              <div>
                <Label>Feedback (Optional)</Label>
                <textarea 
                  value={gradeData.feedback} 
                  onChange={(e) => setGradeData(prev => ({ ...prev, feedback: e.target.value }))}
                  placeholder="Provide feedback to the student..."
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  rows={4}
                />
              </div>
              
              <div className="flex gap-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowGradeModal(false)} className="flex-1">Cancel</Button>
                <Button onClick={handleGrade} disabled={grading} className="flex-1 bg-green-600 hover:bg-green-700">
                  {grading ? 'Saving...' : <><Save className="h-4 w-4 mr-2" />Save Grade</>}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
